import { getAuth } from "firebase/auth";
import html2canvas from "html2canvas-pro";
import jsPDF from "jspdf";
import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { toast } from "react-toastify";
import remarkGfm from "remark-gfm";
import { useLanguage } from "../../context/Language/LanguageContext";
import {
  callAI,
  kAIProviderStorage,
  kProviders,
  keyStorageFor,
  modelStorageFor,
  providerConfig,
} from "../utils/aiProviders";
import { sendShareNotification } from "../utils/emailNotify";
import { saveReport, shareReport } from "../utils/teamPlayerData";
import FollowUpQuestions from "./FollowUpQuestions";
import { reportMarkdownComponents } from "./reportMarkdownComponents";
import Share from "./Share";

// Maps the app's current language code to the English language name used when
// instructing the AI which language to write its report in.
const kLangCodeToAIName = {
  en: "English",
  no: "Norwegian",
  de: "German",
  fr: "French",
  nl: "Dutch",
  it: "Italian",
  es: "Spanish",
  ko: "Korean",
  ja: "Japanese",
  zh: "Chinese (Simplified)",
  vi: "Vietnamese",
  ru: "Russian",
  pt: "Portuguese",
  pl: "Polish",
  th: "Thai",
  tr: "Turkish",
};

const kLanguages = [
  "English",
  "Spanish",
  "French",
  "German",
  "Italian",
  "Portuguese",
  "Dutch",
  "Polish",
  "Russian",
  "Turkish",
  "Arabic",
  "Chinese (Simplified)",
  "Japanese",
  "Korean",
  "Vietnamese",
  "Thai",
];

// Instructs the AI to present comparative/numeric data as charts (rendered by
// AIReportChart) rather than Markdown tables, for better visuals.
const kChartInstruction = [
  "OUTPUT FORMAT — CHARTS ARE REQUIRED:",
  "You MUST visualise numeric and comparative data as charts, NOT as Markdown tables",
  "and NOT only as prose. Include at least 2-4 charts in the report wherever the data",
  "supports it (e.g. team-vs-team comparison of key metrics, sideout/efficiency by",
  "rotation, serve/attack/error breakdown, trends across sets or matches).",
  "",
  "To draw a chart, output a fenced code block whose info string is exactly `chart`",
  "(three backticks followed by the word chart), containing ONLY a JSON object on the",
  "lines between the fences. Example (copy this format exactly):",
  "",
  "```chart",
  '{ "type": "bar", "title": "Sideout % by rotation", "data": [ { "label": "S1", "Team A": 62, "Team B": 48 }, { "label": "S2", "Team A": 55, "Team B": 51 } ] }',
  "```",
  "",
  'Rules: "type" is one of "bar", "line", or "pie". For bar/line, each data row has a',
  '"label" (the category, e.g. a rotation label or set number) plus one or more numeric',
  'series keys (e.g. "Team A", "Kills", "Errors"). For pie, each row is { "label", "value" }.',
  "Keep each chart to at most ~8 data points and ~3 series. Add a one-line interpretation",
  "in prose before or after each chart. Keep narrative analysis, lists and headings as",
  "normal Markdown. The JSON must be valid: double-quoted keys/strings, numbers unquoted,",
  "no trailing commas, no comments. Never put a Markdown table in the report — use a chart",
  "instead.",
].join("\n");

function AIReportPanel({
  title,
  systemPrompt,
  buildPrompt,
  canGenerate,
  notReadyMessage,
  documentTitle,
  generateLabel,
  followUpStorageKey,
}) {
  const { t, lang, allLanguages } = useLanguage();
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [provider, setProvider] = useState("openai");
  const [model, setModel] = useState("gpt-4o-mini");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [translateTo, setTranslateTo] = useState("");
  const [translateNonce, setTranslateNonce] = useState(0);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [showSettings, setShowSettings] = useState(true);
  const [savingReport, setSavingReport] = useState(false);
  const [sharingReport, setSharingReport] = useState(null);
  const [shareNonce, setShareNonce] = useState(0);
  const reportRef = useRef(null);

  // The report is always generated in the app's currently selected language.
  const language =
    kLangCodeToAIName[lang] || allLanguages[lang]?.label || "English";

  // The AI settings are considered configured once a provider, API key and
  // model are all set. When configured, the settings panel starts collapsed.
  const isConfigured = Boolean(provider && apiKey && model);

  const pdfFileName = `${documentTitle || title || "AI Report"}.pdf`;

  // Save the current report as a reusable "My AI Reports" entry (visible on
  // the AI Reports page) and immediately open the Share dialog, the same way
  // matches/playlists are shared — a direct answer to "share an AI report".
  const saveAndShare = async () => {
    if (!result || savingReport) return;
    setSavingReport(true);
    try {
      const saved = await saveReport("adhoc", "self", {
        title: documentTitle || title || "AI Report",
        content: result,
        kind: "custom",
      });
      if (!saved) {
        toast.error(t("Could not save the report."));
        return;
      }
      setShareNonce((n) => n + 1);
      setSharingReport(saved);
    } finally {
      setSavingReport(false);
    }
  };

  const doDoShareReport = async (share) => {
    const report = sharingReport;
    setSharingReport(null);
    if (!share || !report) return;
    const ok = await shareReport(report, share);
    if (!ok) {
      toast.error(t("Could not share the report. Please try again."));
      return;
    }
    if (share.notifyByEmail && share.shareStatus === 2) {
      sendShareNotification({
        to: share.shareUsers,
        itemType: "report",
        itemTitle: report.title,
        sharedByEmail: getAuth().currentUser?.email,
      });
    }
    toast.success(t("Report saved and shared."));
  };

  // Generate and download a real PDF file (rather than opening the browser
  // print dialog, which behaves inconsistently on non-Chrome/Windows browsers).
  const downloadPdf = async () => {
    const el = reportRef.current;
    if (!el) {
      return;
    }
    setGeneratingPdf(true);
    try {
      const shot = await html2canvas(el, {
        scale: 2,
        backgroundColor: "#ffffff",
        useCORS: true,
        onclone: (doc) => {
          // Mirror the print layout: reveal print-only blocks (header) and hide
          // on-screen-only controls (follow-up input).
          doc.querySelectorAll("*").forEach((n) => {
            if (!n.classList) {
              return;
            }
            if (n.classList.contains("print:hidden")) {
              n.style.display = "none";
            }
            if (n.classList.contains("print:block")) {
              n.classList.remove("hidden");
            }
          });
        },
      });
      const pdf = new jsPDF("p", "mm", "a4");
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      // Leave a margin around the content on every page for printing.
      const margin = 10; // mm
      const contentW = pageW - margin * 2;
      const contentH = pageH - margin * 2;
      // How many source (canvas) pixels fit in one printable page height.
      const pxPerMm = shot.width / contentW;
      const pageHeightPx = Math.floor(contentH * pxPerMm);
      let renderedPx = 0;
      let pageIndex = 0;
      while (renderedPx < shot.height) {
        const slicePx = Math.min(pageHeightPx, shot.height - renderedPx);
        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = shot.width;
        pageCanvas.height = slicePx;
        const ctx = pageCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        ctx.drawImage(
          shot,
          0,
          renderedPx,
          shot.width,
          slicePx,
          0,
          0,
          shot.width,
          slicePx
        );
        if (pageIndex > 0) {
          pdf.addPage();
        }
        pdf.addImage(
          pageCanvas.toDataURL("image/png"),
          "PNG",
          margin,
          margin,
          contentW,
          slicePx / pxPerMm
        );
        renderedPx += slicePx;
        pageIndex += 1;
      }
      pdf.save(pdfFileName);
    } catch (e) {
      console.error("PDF generation failed", e);
      toast.error(t("Failed to generate PDF."));
    } finally {
      setGeneratingPdf(false);
    }
  };

  useEffect(() => {
    const storedProvider = localStorage.getItem(kAIProviderStorage);
    const activeProvider =
      storedProvider && kProviders[storedProvider] ? storedProvider : "openai";
    setProvider(activeProvider);

    const storedKey = localStorage.getItem(keyStorageFor(activeProvider));
    setApiKey(storedKey || "");

    const storedModel = localStorage.getItem(modelStorageFor(activeProvider));
    setModel(
      storedModel || kProviders[activeProvider].models[0]
    );

    // Collapse the settings panel on load if everything is already configured.
    setShowSettings(!(storedKey && (storedModel || kProviders[activeProvider].models[0])));
  }, []);

  const saveProvider = (value) => {
    setProvider(value);
    localStorage.setItem(kAIProviderStorage, value);
    setApiKey(localStorage.getItem(keyStorageFor(value)) || "");
    const storedModel = localStorage.getItem(modelStorageFor(value));
    setModel(storedModel || kProviders[value].models[0]);
  };

  const saveKey = (value) => {
    setApiKey(value);
    localStorage.setItem(keyStorageFor(provider), value);
  };

  const saveModel = (value) => {
    setModel(value);
    localStorage.setItem(modelStorageFor(provider), value);
  };

  const callModel = (messages) =>
    callAI({ provider, apiKey, model, messages });

  const analyze = async () => {
    if (!apiKey) {
      toast.error(t("Please enter your API key."));
      return;
    }
    const prompt = buildPrompt();
    if (!prompt) {
      toast.error(notReadyMessage || t("Not enough data to analyse."));
      return;
    }
    setLoading(true);
    setError(null);
    setResult("");
    try {
      const messages = [
        { role: "system", content: systemPrompt },
        { role: "system", content: kChartInstruction },
        { role: "user", content: prompt },
      ];
      if (language && language !== "English") {
        messages.push({
          role: "system",
          content: `Write your entire response in ${language}.`,
        });
      }
      const content = await callModel(messages);
      setResult(content || "No response was returned.");
    } catch (e) {
      setError(e.message || "Failed to analyse.");
    } finally {
      setLoading(false);
    }
  };

  const translate = async (targetLang) => {
    if (!apiKey) {
      toast.error(t("Please enter your API key."));
      return;
    }
    if (!result || !targetLang) {
      return;
    }
    setTranslating(true);
    setError(null);
    try {
      const content = await callModel([
        {
          role: "system",
          content: `You are a professional translator. Translate the following volleyball analysis into ${targetLang}. Preserve the structure, section headings, and bullet points. Only output the translation.`,
        },
        { role: "user", content: result },
      ]);
      if (content) {
        setResult(content);
        setTranslateTo(targetLang);
        setTranslateNonce((n) => n + 1);
      }
    } catch (e) {
      setError(e.message || "Failed to translate report.");
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 p-3 text-sm">
      <div className="rounded-md border border-base-300 bg-base-100">
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-xs font-semibold text-base-content/70"
          onClick={() => setShowSettings((s) => !s)}
        >
          <span>
            {t("AI settings")}
            {!showSettings && (
              <span className="ml-2 font-normal text-base-content/50">
                {providerConfig(provider).label} · {model}
              </span>
            )}
          </span>
          <span className="flex items-center gap-1">
            {!isConfigured && (
              <span className="badge badge-warning badge-xs">{t("Setup needed")}</span>
            )}
            <span>{showSettings ? "▲" : "▼"}</span>
          </span>
        </button>
        {showSettings && (
          <div className="flex flex-col gap-3 border-t border-base-300 p-3">
            <div>
              <label className="block text-xs font-medium text-base-content/70 mb-1">
                {t("AI provider")}
              </label>
              <select
                value={provider}
                onChange={(e) => saveProvider(e.target.value)}
                className="select select-sm select-bordered w-full"
              >
                {Object.keys(kProviders).map((id) => (
                  <option key={id} value={id}>
                    {kProviders[id].label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-base-content/70 mb-1">
                {providerConfig(provider).keyLabel}
              </label>
              <div className="flex gap-1">
                <input
                  type={showKey ? "text" : "password"}
                  value={apiKey}
                  onChange={(e) => saveKey(e.target.value)}
                  placeholder={providerConfig(provider).keyPlaceholder}
                  autoComplete="off"
                  className="input input-sm input-bordered w-full"
                />
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={() => setShowKey((s) => !s)}
                >
                  {showKey ? t("Hide") : t("Show")}
                </button>
              </div>
              <p className="text-[10px] text-base-content/50 mt-1">
                {t(
                  "Your key is stored only in this browser and sent directly to the selected provider."
                )}
              </p>
            </div>

            <div className="flex gap-2">
              <div className="flex-1">
                <label className="block text-xs font-medium text-base-content/70 mb-1">
                  {t("Model")}
                </label>
                <select
                  value={model}
                  onChange={(e) => saveModel(e.target.value)}
                  className="select select-sm select-bordered w-full"
                >
                  {providerConfig(provider).models.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>

      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={analyze}
        disabled={loading || !canGenerate}
      >
        {loading ? (
          <span className="loading loading-spinner loading-xs"></span>
        ) : generateLabel ? (
          t(generateLabel)
        ) : (
          t("Generate AI report")
        )}
      </button>

      {!canGenerate && notReadyMessage && (
        <p className="text-[11px] text-base-content/50">{t(notReadyMessage)}</p>
      )}

      {error && (
        <div className="alert alert-error text-xs p-2">
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-end gap-1">
            <div className="flex items-center gap-1">
              {translating && (
                <span className="loading loading-spinner loading-xs"></span>
              )}
              <select
                className="select select-xs select-bordered"
                value=""
                disabled={translating}
                onChange={(e) => translate(e.target.value)}
                aria-label="Translate report"
              >
                <option value="" disabled>
                  {t("Translate to…")}
                </option>
                {kLanguages.map((lang) => (
                  <option key={lang} value={lang}>
                    {t(lang)}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              className="btn btn-xs"
              onClick={saveAndShare}
              disabled={savingReport}
              title={t("Save this report so you can share it with other users")}
            >
              {savingReport ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                t("Save & Share")
              )}
            </button>
            <button
              type="button"
              className="btn btn-xs"
              onClick={downloadPdf}
              disabled={generatingPdf}
            >
              {generatingPdf ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                t("Download PDF")
              )}
            </button>
          </div>
          <div
            ref={reportRef}
            className="rounded-sm bg-base-100 p-3 text-xs leading-relaxed print:bg-white print:text-black"
          >
            <div className="hidden print:block mb-3 border-b pb-2">
              <h1 className="text-lg font-bold">{title}</h1>
            </div>
            <div className="max-w-3xl [&_h1]:break-inside-avoid [&_h2]:break-inside-avoid [&_h3]:break-inside-avoid [&_li]:break-inside-avoid [&_table]:break-inside-avoid">
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={reportMarkdownComponents}>
                {result}
              </ReactMarkdown>
            </div>
            <FollowUpQuestions
              report={result}
              callModel={callModel}
              systemPrompt={systemPrompt}
              language={language}
              storageKey={followUpStorageKey || "VBLiveFollowUpQuestions"}
              disabled={!apiKey}
              translateTo={translateTo}
              translateNonce={translateNonce}
            />
          </div>
        </div>
      )}

      {sharingReport && (
        <div className="modal modal-open">
          <div className="modal-box max-w-2xl">
            <h3 className="mb-2 text-lg font-bold">
              {t("Share")} — {sharingReport.title}
            </h3>
            <Share
              shareStatus={sharingReport.shareStatus}
              shareUsers={sharingReport.shareUsers}
              currentTime={shareNonce}
              onShare={doDoShareReport}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default AIReportPanel;
